/**
 * Converse CDN URLs (embed, /stream/, /content/, /pub/, /asset/thumb/).
 * Chat reference card images are full HTTPS URLs from BedrockConverse (resolveReferences); the widget does not assemble paths.
 * Hostname is `${amplifyEnvironment}-converse.microcommerce.ai` (prod: no prefix).
 *
 * In the admin and portal apps, pass `amplifyEnvironment` from the Root store (MainModel).
 * On Node servers and Lambdas, use `resolveAmplifyEnvFromProcess()`.
 */
/**
 * Bare hostname (no scheme) from an Amplify-style env key (e.g. huwdev, main, prod).
 */
export function getConverseHostname(amplifyEnvironment) {
    const t = typeof amplifyEnvironment === 'string' ? amplifyEnvironment.trim() : '';
    if (t === '') {
        throw new Error(`unrecognized environment: ${amplifyEnvironment}`);
    }
    const prefix = t === 'prod' ? '' : `${t}-`;
    return `${prefix}converse.microcommerce.ai`;
}
export function getDefaultConverseOrigin(amplifyEnvironment) {
    return `https://${getConverseHostname(amplifyEnvironment)}`;
}
export function getDefaultClientAIStreamUrl(amplifyEnvironment) {
    return `${getDefaultConverseOrigin(amplifyEnvironment)}/stream/`;
}
export function getDefaultClientAIContentUrl(amplifyEnvironment) {
    return `${getDefaultConverseOrigin(amplifyEnvironment)}/content/`;
}
/**
 * Builds the same /asset/thumb/... URL shape as the converse CDN (e.g. admin Web Page preview).
 * Not used for in-chat references — those already carry a full `image` URL from BedrockConverse.
 */
export function buildConverseAssetImageUrl(amplifyEnvironment, kind, organizationID, entityId) {
    const segment = encodeURIComponent(`${organizationID}~${entityId}`);
    return `${getDefaultConverseOrigin(amplifyEnvironment)}/asset/thumb/${kind}/${segment}`;
}
/**
 * Public library asset on the converse CDN: GET /pub/{organizationID~assetId}/{size}.
 * Asset must have access === 'public'; non-public assets return 404 from AssetHandler.
 */
export function buildConversePubAssetUrl(amplifyEnvironment, assetOrganizationID, assetId, size) {
    const org = typeof assetOrganizationID === 'string' ? assetOrganizationID.trim() : '';
    const id = typeof assetId === 'string' ? assetId.trim() : '';
    if (!org || !id) {
        throw new Error('buildConversePubAssetUrl requires assetOrganizationID and assetId');
    }
    const composite = encodeURIComponent(`${org}~${id}`);
    return `${getDefaultConverseOrigin(amplifyEnvironment)}/pub/${composite}/${encodeURIComponent(size)}`;
}
/**
 * Prefer `AMPLIFY_ENV`; fall back to `ENV` for legacy Amplify Lambdas.
 * Node/SSR only — do not import from browser bundles (no `process.env` at runtime).
 */
export function resolveAmplifyEnvFromProcess() {
    if (typeof process === 'undefined' || !process.env) {
        throw new Error('unrecognized environment: process.env is not available');
    }
    const e = process.env.AMPLIFY_ENV?.trim() || process.env.ENV?.trim();
    if (!e) {
        throw new Error(`unrecognized environment: AMPLIFY_ENV/ENV missing (AMPLIFY_ENV=${process.env.AMPLIFY_ENV})`);
    }
    return e;
}
//# sourceMappingURL=defaultClientAIUrls.js.map